/**
 * VozidloOdkaz — the car of a fleet entry as a link to its detail (the used-car or the new-car page), wherever the rental module names a
 * car (the calendar's rows, the reservation list, the fleet). The link exists only where it can work: the entry's stock link is the car's
 * canonical row (`stav_vazby` ok), the member reads that stock (`ojeta_vozidla` / `vozidla`), and the car's firma is one whose stock the
 * member may read — the active firma, or another of the group the server's scope (`GET /v1/skupina-rozsah`) names; that page then opens
 * read-only in the car's own firma. Anywhere else the car stays plain text, never a link that ends in "not found".
 */

import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useModuleAccess } from '@/lib/permissions';
import { useSkupinaRozsahQuery } from '@/modules/skupiny/rozsah';
import type { FleetEntry } from './api';

type Auto = Pick<FleetEntry, 'zdroj_typ' | 'id_zdroje' | 'id_firmy_vlastnika' | 'stav_vazby'>;
export type VozidloCil = { kind: 'nove' | 'ojete'; id: number };

/** Where the car's page is, or null when the member has no way to it (pure: the answers of the access checks come in). */
export function vozidloCil(e: Auto, smiCist: { nove: boolean; ojete: boolean }, firmy: { nove: number[]; ojete: number[] }): VozidloCil | null {
  if (e.stav_vazby !== 'ok') return null;
  const kind = e.zdroj_typ;
  if (!smiCist[kind] || !firmy[kind].includes(e.id_firmy_vlastnika)) return null;
  return { kind, id: e.id_zdroje };
}

export function useVozidloCil(): (e: Auto) => VozidloCil | null {
  const { can } = useModuleAccess();
  const smiNove = can('vozidla', 'read');
  const smiOjete = can('ojeta_vozidla', 'read');
  const nove = useSkupinaRozsahQuery('vozidla', smiNove);
  const ojete = useSkupinaRozsahQuery('ojeta_vozidla', smiOjete);
  const ids = (d: typeof nove.data) => (d ? d.firmy.map((f) => f.id_firmy) : []);
  return (e) => vozidloCil(e, { nove: smiNove, ojete: smiOjete }, { nove: ids(nove.data), ojete: ids(ojete.data) });
}

const TRIDA = 'text-ink underline-offset-2 hover:underline focus-visible:underline';

export function VozidloOdkaz({ entry, children, className = '' }: { entry: Auto | null | undefined; children: ReactNode; className?: string }) {
  const cil = useVozidloCil()(entry ?? { zdroj_typ: 'ojete', id_zdroje: 0, id_firmy_vlastnika: 0, stav_vazby: 'zastarala' });
  if (!entry || !cil) return <>{children}</>;
  const spolecne = { className: `${TRIDA} ${className}`, title: 'Otevřít vozidlo', onClick: (e: { stopPropagation: () => void }) => e.stopPropagation() };
  return cil.kind === 'nove' ? (
    <Link to="/vozidla/$id" params={{ id: String(cil.id) }} {...spolecne}>
      {children}
    </Link>
  ) : (
    <Link to="/ojeta-vozidla/$id" params={{ id: String(cil.id) }} {...spolecne}>
      {children}
    </Link>
  );
}
